import React from "react";
import { useS3Upload } from "@src/features/app/hooks/useS3Upload";
import { FileRejection, useDropzone } from "react-dropzone";
import { useToast } from "@src/lib/hooks/useToast";
import {
  ACCEPTED_FILE_TYPES,
  ACCEPTED_FILE_TYPES_GROUPED,
  MAX_FILE_SIZE_BYTES,
} from "@src/lib/utils/files";
import { cn } from "@src/lib/utils/cn";
import {
  FileDropZoneContext,
  FileDropZoneContextValue,
} from "@src/lib/ui/FileDropZoneSurface";

type Props = {
  setMediaUrl: (url: string) => void;
  children: React.ReactNode;
  disabled?: boolean;
  onClick?: () => void;
  className?: string;
  isFileUploading: boolean;
  setIsFileUploading: (isUploading: boolean) => void;
};

export const UPLOAD_FAILED_MESSAGE = "That file couldn't be uploaded.";
const FILE_TOO_LARGE_MESSAGE = "That file is too large.";
// Matches useS3Upload's size toast.
const FILE_TOO_LARGE_TOAST =
  "That file is too large. If it's a video, try uploading it to Vimeo or YouTube and pasting in the link.";

export function rejectionMessage(rejections: FileRejection[]): string {
  if (rejections.length > 1) return "Drop one file at a time.";
  const code = rejections[0]?.errors[0]?.code;
  if (code === "file-too-large") return FILE_TOO_LARGE_MESSAGE;
  if (code === "file-invalid-type") return "That file type isn't supported.";
  return "That file can't be uploaded.";
}

const FileDropZone: React.FC<Props> = (props) => {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const { addErrorToast } = useToast();
  const [error, setError] = React.useState<string | null>(null);
  const { setMediaUrl, setIsFileUploading } = props;
  const { handleFileUpload, isUploading: loading } = useS3Upload({
    onUploadComplete: (upload) => {
      setMediaUrl(upload.url);
      setIsFileUploading(false);
    },
    // useS3Upload toasts the failure itself; clear the spinner so the zone
    // can be used again.
    onUploadFailed: () => {
      setIsFileUploading(false);
      setError(UPLOAD_FAILED_MESSAGE);
    },
  });

  const reject = React.useCallback(
    (message: string) => {
      setError(message);
      addErrorToast({
        message:
          message === FILE_TOO_LARGE_MESSAGE ? FILE_TOO_LARGE_TOAST : message,
        callsite: "file_drop_zone_rejected",
      });
    },
    [addErrorToast],
  );

  const upload = React.useCallback(
    (file: File) => {
      // Drops are size-checked by react-dropzone; picked files aren't.
      if (file.size > MAX_FILE_SIZE_BYTES) {
        reject(FILE_TOO_LARGE_MESSAGE);
        return;
      }
      setError(null);
      handleFileUpload(file);
    },
    [handleFileUpload, reject],
  );

  const { disabled = false } = props;
  // One upload at a time: a second drop or pick while busy is ignored.
  const busy = props.isFileUploading || loading;
  const locked = disabled || busy;

  const onDrop = React.useCallback(
    <T extends File>(acceptedFiles: T[], rejections: FileRejection[]) => {
      if (locked) return;
      if (acceptedFiles.length === 1) {
        upload(acceptedFiles[0]);
      } else if (rejections.length > 0) {
        reject(rejectionMessage(rejections));
      }
    },
    [locked, upload, reject],
  );
  // Never pass `disabled` to useDropzone: it nulls the root's drop handlers,
  // so nothing calls preventDefault and the browser opens the dropped file
  // (navigating away). Stay enabled, ignore drops in onDrop, and hide the
  // drag look while locked.
  const {
    getRootProps,
    isDragActive: rawDragActive,
    isDragReject: rawDragReject,
  } = useDropzone({
    useFsAccessApi: true,
    multiple: false,
    noClick: true,
    noKeyboard: true,
    maxSize: MAX_FILE_SIZE_BYTES,
    onDrop,
    accept: ACCEPTED_FILE_TYPES_GROUPED,
  });
  const { onClick } = props;
  const isDragActive = !locked && rawDragActive;
  const isDragReject = !locked && rawDragReject;
  const maybeOpenFilePicker = React.useCallback(() => {
    if (locked) return;
    if (onClick) {
      onClick();
    } else {
      fileInputRef.current?.click();
    }
  }, [locked, onClick]);
  React.useEffect(() => {
    if (!props.isFileUploading && loading) {
      props.setIsFileUploading(true);
    }
  }, [loading, props.isFileUploading, props]);

  const contextValue = React.useMemo<FileDropZoneContextValue>(
    () => ({
      isDragActive,
      isDragReject,
      isUploading: busy,
      disabled,
      error,
    }),
    [isDragActive, isDragReject, busy, disabled, error],
  );

  return (
    <FileDropZoneContext.Provider value={contextValue}>
      <div
        className="flex flex-1 self-stretch"
        data-drag-active={isDragActive || undefined}
        {...getRootProps()}
      >
        <div
          className={cn(
            "flex flex-1 flex-col items-center justify-center self-stretch",
            !disabled && "cursor-pointer",
            props.className,
          )}
          onClick={maybeOpenFilePicker}
        >
          <input
            type="file"
            multiple={false}
            ref={fileInputRef}
            disabled={props.disabled}
            style={{ display: "none" }}
            accept={ACCEPTED_FILE_TYPES.toString()}
            onChange={(e) => {
              const files = Array.from(e.target.files ?? []);
              // Reset so picking the same file again still fires a change.
              e.target.value = "";
              if (files.length === 1) {
                upload(files[0]);
              }
            }}
          />
          {props.children}
        </div>
      </div>
    </FileDropZoneContext.Provider>
  );
};

export default FileDropZone;
